import { PostHogMaskView } from "@-/com.npmjs.posthog-react-native";
import { captureAnalyticsEvent, noteAuthMethod } from "@-/dev.improve.haniverse.lib.analytics";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { ResetPasswordForm } from "@-/dev.improve.haniverse.components.ui.reset-password-form";
import { SignInForm } from "@-/dev.improve.haniverse.components.ui.sign-in-form";
import { SignUpForm } from "@-/dev.improve.haniverse.components.ui.sign-up-form";
import { SignInReplaceWarningDialog } from "@-/dev.improve.haniverse.components.ui.sign-in-replace-warning-dialog";
import { TwoFactorChallengeForm } from "@-/dev.improve.haniverse.components.ui.two-factor-challenge-form";
import { VerifyEmailForm } from "@-/dev.improve.haniverse.components.ui.verify-email-form";
import { getAuthClient, interactiveSignInDeadline } from "@-/dev.improve.haniverse.lib.auth.client";
import {
  bootstrapActorSyncForSignIn,
  markSignInForActorReset,
  markSignUpForGuestReplay,
} from "@-/dev.improve.haniverse.lib.sync-client";
import { setDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { hasLocalStudyData } from "@-/dev.improve.haniverse.lib.database.export";
import { useGetActorDatabaseConnection } from "@-/dev.improve.haniverse.contexts.database.actor";
import { withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import { useReadinessDeadline } from "@-/dev.improve.haniverse.lib.readiness-handshake";
import { isAnyObject } from "@-/com.npmjs.is-what";
import { isNonEmpty, type ReadonlyArrayMinLength } from "@-/com.npmjs.ts-array-length";
import { toast } from "@-/com.npmjs.sonner";
import { useIsMutating, useMutation, useQueryClient } from "@-/com.npmjs.tanstack__react-query";
import * as AppleAuthentication from "@-/com.npmjs.expo-apple-authentication";
import * as Linking from "@-/com.npmjs.expo-linking";
import { useRouter } from "@-/com.npmjs.expo-router";
import { Platform, View } from "@-/com.npmjs.react-native";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { authErrorMessage } from "#auth-error-message";

/**
 * Records the address credentials just succeeded for as the device's account email. The native
 * `(app)` layout requires one, and the direct leaves below can run before Better Auth publishes the
 * session that carries it (the app gate would otherwise send the user straight back here).
 */
function recordAccountEmail(email: string): void {
  const trimmed = email.trim();
  if (trimmed.length > 0) {
    setDeviceSetting("accountEmail", trimmed);
  }
}

/**
 * Refreshing the session after a successful credential or OAuth exchange is one get-session round
 * trip, and it only PUBLISHES a session the server already granted. Past this bound the form stops
 * waiting on it (a password sign-in then leaves anyway) instead of showing "Signing in…" forever.
 */
const SESSION_REFRESH_DEADLINE_MS = 15_000;
/**
 * The pre-sign-in "is there local study data to lose?" probe opens the actor connection and runs one
 * local query: milliseconds when healthy. It is best-effort, so on expiry sign-in proceeds without
 * the warning, exactly as it already does when the probe throws.
 */
const LOCAL_STUDY_DATA_PROBE_DEADLINE_MS = 5000;
/**
 * The leave-after-sign-in effect holds while an auth mutation is still finishing, so it never
 * navigates against the still-guest database. Once signed in, what remains of every such mutation
 * is bounded (its server round trips, its session refresh above), so this only catches a wedge
 * elsewhere: past it the form leaves anyway rather than trapping a signed-in user on the auth screen.
 */
const SIGNED_IN_LEAVE_HOLD_DEADLINE_MS = SESSION_REFRESH_DEADLINE_MS + 5000;

export type BlockAuthFormMode = "signIn" | "signUp" | "reset" | "verifyEmail" | "twoFactor";

/**
 * Initial screen a route requests when it hosts the form. `reset` / `verifyEmail` exist so the dedicated `auth/reset-password`
 * and `auth/verify-email` routes (and email deep-links) can open straight onto
 * those screens; the `twoFactor` challenge is never an entry mode (it's only
 * reached mid-sign-in), so it's intentionally excluded.
 */
export type BlockAuthFormInitialMode = "signIn" | "signUp" | "reset" | "verifyEmail";

export type BlockAuthFormSocialProvider = {
  onPress: () => void;
  pending: boolean;
  provider: "apple" | "google";
};

export type BlockAuthFormSocialConnectionsApi = {
  compact: boolean;
  disabled: boolean;
  /**
   * The providers to show, in display order. Never empty: when no provider is available the form
   * skips `renderSocialConnections` entirely (and the auth form drops its "or" divider).
   */
  providers: ReadonlyArrayMinLength<BlockAuthFormSocialProvider, 1>;
};

export type BlockAuthFormProps = {
  /**
   * Sign-in and sign-up render only their email + password fields (see `AuthFormProps.bare`); the
   * host supplies the mode toggle and provider buttons, so `renderSocialConnections` is not used.
   */
  bare?: boolean;
  /** Uses the standalone card's centred heading and divided mode-switch footer. */
  card?: boolean;
  /** Fits the dedicated tabbed auth screens without scrolling. */
  compact?: boolean;
  /**
   * Pre-fills the email field. The auth sheet passes the address a guest recorded through
   * "Get Started", so activating the account only needs a password and the emailed code.
   */
  initialEmail?: string;
  /**
   * Which screen the form first renders. The dedicated `auth/*` routes pass this
   * so each opens directly in its mode. Mode still switches in-place
   * afterwards (footer toggle, forgot→reset, sign-up→verify-email,
   * password→two-factor challenge).
   */
  initialMode: BlockAuthFormInitialMode;
  /**
   * Called whenever the form swaps between auth screens. Hosts can use this to
   * keep route chrome (for example the nav header title) aligned with the
   * current in-place screen.
   */
  onModeChange?: (mode: BlockAuthFormMode) => void;
  /**
   * How to leave this screen once a session is established (email/password,
   * OAuth, verify-email or two-factor). Defaults to `router.back()`, which is
   * correct when the form is PUSHED on top of the app (e.g. the Settings-tab
   * sign-in page) — popping just dismisses back into the app.
   *
   * The shared auth sheet MUST override this: it is presented over the current
   * screen rather than pushed, so `router.back()` would pop whatever was beneath it
   * (and from the welcome gate, which sits outside the `(app)` tab group, it would
   * never reach the app). It closes itself and, from the gate, replaces into Learn.
   */
  onSignedIn?: () => void;
  /**
   * Renders the Apple / Google sign-in controls. Auth routes supply this via
   * `@-/dev.improve.haniverse.routes.nav.app.auth._components.social-sign-in` so the UI package
   * does not reach into auth `_components` packages.
   */
  renderSocialConnections?: (api: BlockAuthFormSocialConnectionsApi) => ReactNode;
  /** A rejected cached session must be replaced before this form can dismiss. */
  requireFreshSession?: boolean;
};

/**
 * Better-Auth-wired host for the React Native Reusables auth blocks.
 *
 * Owns the email/password, OAuth, forgot-password, reset-password and two-factor
 * challenge logic and drives the presentational `SignInForm` / `SignUpForm` /
 * `ResetPasswordForm` / `VerifyEmailForm` / `TwoFactorChallengeForm` block
 * components. The blocks stay presentational; this component owns the form state
 * and mutations.
 */
export function BlockAuthForm({
  bare = false,
  card = false,
  compact = false,
  initialEmail = "",
  initialMode,
  onModeChange,
  onSignedIn,
  renderSocialConnections,
  requireFreshSession = false,
}: BlockAuthFormProps) {
  const router = useRouter();

  // How to leave this screen once a session exists. Defaults to popping back
  // (correct for a form PUSHED on top of the app, e.g. the Settings-tab sign-in
  // page). The shared auth sheet overrides this with a handler that closes the
  // sheet (and replaces into Learn from the welcome gate). See `onSignedIn`.
  const leaveAfterSignIn = useCallback(() => {
    if (onSignedIn != null) {
      onSignedIn();
      return;
    }
    if (router.canGoBack()) {
      router.back();
    }
  }, [onSignedIn, router]);

  // Once a session is established (email/password OR OAuth), this sign-in page
  // has done its job — leave it so the screen dismisses after a successful
  // sign-up/sign-in. The OAuth flows have no `onSuccess` navigation of their own,
  // so this reactive effect is what dismisses them.
  const { data: session, refetch: refetchSession } = getAuthClient().useSession();
  const [rejectedSessionId] = useState(() =>
    requireFreshSession ? session?.session.id : undefined,
  );
  const isSignedIn =
    session?.user != null && (!requireFreshSession || session.session.id !== rejectedSessionId);
  const queryClient = useQueryClient();
  const pendingAuthMutations = useIsMutating({ mutationKey: ["auth"] });
  // The hold below is a readiness wait like any other: bounded (see SIGNED_IN_LEAVE_HOLD_DEADLINE_MS).
  const hasLeaveHoldExpired = useReadinessDeadline({
    deadlineMs: SIGNED_IN_LEAVE_HOLD_DEADLINE_MS,
    operation: "auth mutation to finish before leaving after sign-in",
    waiting: isSignedIn && pendingAuthMutations > 0,
  });

  // Keep the freshest `leaveAfterSignIn` in a ref so the leave-effect can call it
  // WITHOUT listing it as a dependency. `leaveAfterSignIn`'s identity churns every
  // render in the auth sheet opened from the welcome gate: its `onSignedIn` closes over
  // the learn-summary page navigator, whose memo is rebuilt whenever
  // `useLearnSummaryPageQueryOptions()` returns a fresh `queryOptions({…})` object —
  // and that happens on every render during the post-sign-in actor-sync burst
  // (`markSignInForActorReset()` wipes+resyncs the actor DB → rapid
  // `LEARN_SUMMARY_TABLES` change-version bumps). If the effect depended on
  // `leaveAfterSignIn`, that churn would re-fire it every render and re-run
  // `router.replace` in a tight loop → "Maximum update depth exceeded". Same class
  // as the learn-summary redirect-effect href-identity loop.
  const leaveAfterSignInRef = useRef(leaveAfterSignIn);
  leaveAfterSignInRef.current = leaveAfterSignIn;

  // Latch so we leave EXACTLY once per signed-in transition, regardless of how many
  // times this component re-renders while signed in (see the churn note above).
  const hasLeftRef = useRef(false);
  useEffect(() => {
    if (!isSignedIn) {
      // Reset when the session goes away (e.g. sign-out then a fresh sign-in on the
      // same mounted form) so the next sign-in leaves again.
      hasLeftRef.current = false;
      return;
    }
    if (hasLeftRef.current) {
      return;
    }
    // Better Auth can publish its session before the credential mutation resumes
    // and marks the local database for reset/replay. Do not bootstrap or navigate
    // against the still-guest state in that interval. OAuth callbacks without a
    // pending local mutation continue through this effect normally.
    if (!hasLeaveHoldExpired && pendingAuthMutations > 0) {
      return;
    }
    hasLeftRef.current = true;
    // Kick off the deck bootstrap DETACHED and navigate immediately: provision the
    // per-user Turso DB, reopen in sync mode, and pull the account's deck — all in the
    // background. Screens that need the synced data don't need this to have finished:
    // `markSignInForActorReset` installed a read gate over the actor tables, so their
    // queries simply stay in the ordinary React Query loading state until the initial
    // pull settles. The completed bootstrap is still what lets a subsequent
    // auto-sync-OFF launch open local-only (the deck is already present). This call is
    // also what triggers provisioning for OAuth sign-ins, which have no explicit
    // auth-success handler bumping the auth-change signal. Single-flight inside, so it
    // safely races `useActorSync`'s own provisioning effect.
    void bootstrapActorSyncForSignIn(queryClient).catch(() => {
      // A failed/timed-out bootstrap is recorded in sync-status; the app shows whatever
      // synced and background sync keeps draining.
    });
    leaveAfterSignInRef.current();
  }, [hasLeaveHoldExpired, isSignedIn, pendingAuthMutations, queryClient]);

  // Publish the new session to the mounted observer, bounded (see SESSION_REFRESH_DEADLINE_MS).
  // Resolves `false` when the refresh failed or ran out of time; never rejects.
  const refreshSessionBounded = useCallback(async (): Promise<boolean> => {
    try {
      await withDeadline(
        { deadline: SESSION_REFRESH_DEADLINE_MS, operation: "auth session refresh after sign-in" },
        async () => {
          await refetchSession();
        },
      );
      return true;
    } catch (error) {
      logger(import.meta.location).warn(
        "[auth] session refresh after sign-in did not finish",
        error,
      );
      return false;
    }
  }, [refetchSession]);

  const [mode, setMode] = useState<BlockAuthFormMode>(initialMode);
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [twoFactorUseBackupCode, setTwoFactorUseBackupCode] = useState(false);
  // Credentials held back while the "signing in replaces this device's data" dialog
  // is open. Non-null ⇒ the dialog is showing.
  const [pendingSignInValues, setPendingSignInValues] = useState<{
    email: string;
    password: string;
  } | null>(null);

  const getActorConnectionForExport = useGetActorDatabaseConnection();

  useEffect(() => {
    onModeChange?.(mode);
  }, [mode, onModeChange]);

  // Web's root is the marketing page. OAuth reloads the document, so return directly to app
  // startup. Native keeps its root deep link, which already enters app startup.
  const oauthCallbackPath = useMemo(
    () => Linking.createURL(Platform.OS === "web" ? "/startup" : "/"),
    [],
  );

  const oAuthGoogleMutation = useMutation({
    meta: { deadline: interactiveSignInDeadline() },
    mutationFn: async () => {
      noteAuthMethod("google");
      const { error } = await getAuthClient().signIn.social({
        callbackURL: oauthCallbackPath,
        provider: "google",
      });
      if (error != null) {
        toast.error(authErrorMessage(error));
        return;
      }
      // Refresh the mounted observer too: native OAuth can finish before its
      // cookie-backed session is published. The signed-in effect navigates as
      // soon as that session appears, without waiting for the account sync.
      await refreshSessionBounded();
    },
    mutationKey: ["auth", "oAuthGoogle"],
    onError: (error) => {
      logger(import.meta.location).error("Better Auth Google sign-in failed", error);
      toast.error(authErrorMessage(error));
    },
  });

  const oAuthAppleMutation = useMutation({
    meta: { deadline: interactiveSignInDeadline() },
    mutationFn: async () => {
      if (Platform.OS !== "ios") {
        return;
      }
      const credential = await AppleAuthentication.signInAsync({
        requestedScopes: [
          AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
          AppleAuthentication.AppleAuthenticationScope.EMAIL,
        ],
      });
      if (credential.identityToken == null || credential.identityToken.length === 0) {
        toast.error("Apple did not return an identity token.");
        return;
      }
      noteAuthMethod("apple");
      const { error } = await getAuthClient().signIn.social({
        callbackURL: oauthCallbackPath,
        idToken: { token: credential.identityToken },
        provider: "apple",
      });
      if (error != null) {
        toast.error(authErrorMessage(error));
      }
    },
    mutationKey: ["auth", "oAuthApple"],
    onError: (error) => {
      if (
        isAnyObject(error) &&
        "code" in error &&
        Reflect.get(error, "code") === "ERR_REQUEST_CANCELED"
      ) {
        return;
      }
      logger(import.meta.location).error("Better Auth Apple sign-in failed", error);
      toast.error(authErrorMessage(error));
    },
  });

  // Send (or resend) the sign-up verification email. Triggered when sign-in
  // fails because the email isn't verified yet, and from the verify-email
  // screen's "Resend" button.
  const sendVerificationEmail = useCallback(
    async (targetEmail: string) => {
      const { error } = await getAuthClient().sendVerificationEmail({
        callbackURL: oauthCallbackPath,
        email: targetEmail,
      });
      if (error != null) {
        toast.error(authErrorMessage(error));
      }
    },
    [oauthCallbackPath],
  );

  const emailPasswordMutation = useMutation({
    mutationFn: async (values: {
      email: string;
      password: string;
    }): Promise<"signedIn" | "verifyEmail" | "twoFactor" | "none"> => {
      const trimmed = values.email.trim();
      if (trimmed.length === 0 || values.password.length === 0) {
        toast.error("Enter email and password.");
        return "none";
      }
      noteAuthMethod("email");
      if (mode === "signIn") {
        const { data, error } = await getAuthClient().signIn.email({
          email: trimmed,
          password: values.password,
        });
        if (error != null) {
          // `requireEmailVerification` rejects unverified sign-ins with 403 and
          // (re)sends the verification email. Route the user to the verify-email
          // screen instead of showing a generic error.
          if (error.status === 403) {
            setVerifyCode("");
            setMode("verifyEmail");
            toast.info("Verify your email to finish signing in. We sent you a new code.");
            return "none";
          }
          toast.error(authErrorMessage(error));
          return "none";
        }
        // When the account has two-factor auth enabled, Better Auth's `twoFactor`
        // plugin returns NO session and `data.twoFactorRedirect: true` instead —
        // the password is verified but a TOTP / backup code is still required.
        // Route to the challenge screen; the verify mutation below establishes the
        // session and marks the actor DB for reset there.
        if (
          isAnyObject(data) &&
          "twoFactorRedirect" in data &&
          Reflect.get(data, "twoFactorRedirect") === true
        ) {
          setTwoFactorCode("");
          setTwoFactorUseBackupCode(false);
          setMode("twoFactor");
          return "twoFactor";
        }
        // Signing into an EXISTING account → the server is the source of truth.
        // Mark the actor DB to be wiped before the sync-mode reload so it
        // bootstraps the account's cloud deck. Sign-up below preserves local.
        markSignInForActorReset();
        recordAccountEmail(trimmed);
        return "signedIn";
      }
      const { data, error } = await getAuthClient().signUp.email({
        email: trimmed,
        name: trimmed.split("@")[0] ?? "Han user",
        password: values.password,
      });
      if (error != null) {
        toast.error(authErrorMessage(error));
        return "none";
      }
      // The server only requires email verification in PRODUCTION
      // (`isEmailVerificationRequired()` is gated to EXPO_PUBLIC_APP_VARIANT==="production" —
      // see get-auth-server.ts). When verification IS required, `signUp.email`
      // returns NO session and the server emails a code → go to the verify-email
      // screen. When it's NOT required (dev / staging), the server auto-signs-in
      // and the response carries a session `token` → treat as signed in, exactly
      // like a successful sign-in, so the user lands in the app instead of a
      // dead-end "enter the code we emailed you" screen no code ever arrives for.
      const hasSession =
        isAnyObject(data) && typeof data.token === "string" && data.token.length > 0;
      captureAnalyticsEvent("account_sign_up_submitted", {
        method: "email",
        requires_verification: !hasSession,
      });
      if (hasSession) {
        // New account → preserve any local study done before sign-up and push it
        // (do NOT mark the actor DB for reset, unlike the existing-account sign-in
        // path above). On web that preservation is an explicit step: guest study
        // lives in its own `guest.db`, so the rows are replayed into the freshly
        // provisioned account and pushed. Also signals the session change so
        // `useActorSync` provisions the per-user Turso DB even when the reactive
        // session hook doesn't update (Android Release — see authChangeSignal).
        markSignUpForGuestReplay();
        recordAccountEmail(trimmed);
        return "signedIn";
      }
      return "verifyEmail";
    },
    mutationKey: ["auth", "emailPassword"],
    onError: (error) => {
      logger(import.meta.location).error("Better Auth email/password flow failed", error);
      toast.error(authErrorMessage(error));
    },
    onSuccess: async (result) => {
      if (result === "verifyEmail") {
        setVerifyCode("");
        setMode("verifyEmail");
        toast.success("Account created. Enter the code we emailed you to verify your address.");
        return;
      }
      if (result !== "signedIn") {
        return;
      }
      // Update the reactive session atom before leaving this mounted form. Navigating immediately
      // after the response can unmount the only active session observer before Better Auth
      // publishes its cookie-backed session, leaving Settings signed out even though auth
      // succeeded. The signed-in effect above owns actor bootstrap and the single navigation.
      if (!(await refreshSessionBounded())) {
        // The server granted the session; only its local publish is late. Leave now, latched and
        // bootstrapping exactly as the signed-in effect would, instead of stranding the user on a
        // form whose work is done (`onSuccess` is outside the query client's mutation bound).
        hasLeftRef.current = true;
        void bootstrapActorSyncForSignIn(queryClient).catch(() => {
          // Recorded in sync-status, as in the signed-in effect.
        });
        leaveAfterSignIn();
      }
    },
  });

  /**
   * Sign-in entry point. Signing into an existing account replaces this device's
   * local study data with the account's cloud deck, so warn FIRST when there is
   * anything to lose — before the credentials are submitted, since afterwards the
   * user is already signed in and the choice is moot.
   *
   * The probe is best-effort: any failure falls through to signing in directly
   * rather than blocking the user out of their account.
   */
  const [isCheckingLocalStudyData, setIsCheckingLocalStudyData] = useState(false);
  const requestSignIn = useCallback(
    async (values: { email: string; password: string }) => {
      // The probe is part of the submit: it shows the pending label and blocks a second press.
      setIsCheckingLocalStudyData(true);
      try {
        const hasData = await withDeadline(
          {
            deadline: LOCAL_STUDY_DATA_PROBE_DEADLINE_MS,
            operation: "sign-in local study data probe",
          },
          async () => hasLocalStudyData(await getActorConnectionForExport()),
        );
        if (hasData) {
          setPendingSignInValues(values);
          return;
        }
      } catch (error) {
        logger(import.meta.location).warn(
          "[auth] could not check for local study data; signing in without the warning",
          error,
        );
      } finally {
        setIsCheckingLocalStudyData(false);
      }
      emailPasswordMutation.mutate(values);
    },
    [emailPasswordMutation, getActorConnectionForExport],
  );

  const forgotPasswordMutation = useMutation({
    mutationFn: async () => {
      const trimmed = email.trim();
      if (trimmed.length === 0) {
        toast.error("Enter your email first.");
        return;
      }
      const { error } = await getAuthClient().requestPasswordReset({
        email: trimmed,
        redirectTo: oauthCallbackPath,
      });
      if (error != null) {
        toast.error(authErrorMessage(error));
        return;
      }
      setResetCode("");
      setResetPassword("");
      setMode("reset");
      toast.success("If an account exists, a reset email was sent. Check your inbox for the code.");
    },
    mutationKey: ["auth", "forgotPassword"],
    onError: (error) => {
      logger(import.meta.location).error("Better Auth forgot-password request failed", error);
      toast.error(authErrorMessage(error));
    },
  });

  const passwordResetMutation = useMutation({
    mutationFn: async (values: { code: string; password: string }) => {
      if (values.code.trim().length === 0) {
        toast.error("Enter the reset code from your email.");
        return;
      }
      if (values.password.length === 0) {
        toast.error("Choose a new password.");
        return;
      }
      const { error } = await getAuthClient().resetPassword({
        newPassword: values.password,
        token: values.code.trim(),
      });
      if (error != null) {
        toast.error(authErrorMessage(error));
        return;
      }
      setMode("signIn");
      setResetCode("");
      setResetPassword("");
      toast.success("Password updated. You can sign in with your new password.");
    },
    mutationKey: ["auth", "passwordReset"],
    onError: (error) => {
      logger(import.meta.location).error("Better Auth password reset failed", error);
      toast.error(authErrorMessage(error));
    },
  });

  const verifyEmailMutation = useMutation({
    mutationFn: async (values: { code: string }): Promise<boolean> => {
      const token = values.code.trim();
      if (token.length === 0) {
        toast.error("Enter the verification code from your email.");
        return false;
      }
      noteAuthMethod("email");
      const { error } = await getAuthClient().verifyEmail({ query: { token } });
      if (error != null) {
        toast.error(authErrorMessage(error));
        return false;
      }
      // Verifying establishes a session. Treat the now-signed-in account as the
      // source of truth, same as a fresh sign-in.
      markSignInForActorReset();
      recordAccountEmail(email);
      return true;
    },
    mutationKey: ["auth", "verifyEmail"],
    onError: (error) => {
      logger(import.meta.location).error("Better Auth email verification failed", error);
      toast.error(authErrorMessage(error));
    },
    onSuccess: (succeeded) => {
      if (!succeeded) {
        return;
      }
      captureAnalyticsEvent("email_verified", {});
      toast.success("Email verified.");
      void getAuthClient().getSession({ query: { disableCookieCache: true } });
      // This path navigates directly (the session refresh above may not flip the
      // reactive `useSession` hook — Android Release, see use-actor-sync.ts). Latch
      // the signed-in effect so a later session flip doesn't navigate a second time.
      hasLeftRef.current = true;
      leaveAfterSignIn();
    },
  });

  const resendVerificationMutation = useMutation({
    mutationFn: async () => {
      const trimmed = email.trim();
      if (trimmed.length === 0) {
        toast.error("Enter your email first.");
        return;
      }
      await sendVerificationEmail(trimmed);
      toast.success("Verification code sent.");
    },
    mutationKey: ["auth", "resendVerification"],
    onError: (error) => {
      logger(import.meta.location).error("Better Auth resend verification failed", error);
      toast.error(authErrorMessage(error));
    },
  });

  // The sign-in two-factor challenge. Reached only when a password sign-in
  // returned `twoFactorRedirect` (the account has 2FA enabled). Verifying a TOTP
  // or backup code establishes the session — treat the now-signed-in account as
  // the source of truth (mark the actor DB for reset, same as a fresh sign-in).
  const twoFactorVerifyMutation = useMutation({
    mutationFn: async (values: { code: string }): Promise<boolean> => {
      const code = values.code.trim();
      if (code.length === 0) {
        toast.error(
          twoFactorUseBackupCode
            ? "Enter a backup code."
            : "Enter the code from your authenticator app.",
        );
        return false;
      }
      noteAuthMethod("email_2fa");
      const { error } = twoFactorUseBackupCode
        ? await getAuthClient().twoFactor.verifyBackupCode({ code })
        : await getAuthClient().twoFactor.verifyTotp({ code });
      if (error != null) {
        toast.error(authErrorMessage(error));
        return false;
      }
      markSignInForActorReset();
      recordAccountEmail(email);
      return true;
    },
    mutationKey: ["auth", "twoFactorVerify"],
    onError: (error) => {
      logger(import.meta.location).error("Better Auth two-factor verification failed", error);
      toast.error(authErrorMessage(error));
    },
    onSuccess: (succeeded) => {
      if (!succeeded) {
        return;
      }
      void getAuthClient().getSession({ query: { disableCookieCache: true } });
      // Direct navigation with the signed-in effect latched — same reasoning as the
      // verify-email success handler above.
      hasLeftRef.current = true;
      leaveAfterSignIn();
    },
  });

  const busy =
    oAuthGoogleMutation.isPending ||
    oAuthAppleMutation.isPending ||
    emailPasswordMutation.isPending ||
    forgotPasswordMutation.isPending ||
    passwordResetMutation.isPending ||
    verifyEmailMutation.isPending ||
    resendVerificationMutation.isPending ||
    twoFactorVerifyMutation.isPending ||
    isCheckingLocalStudyData;

  const showApple = Platform.OS === "ios";

  const socialProviders: BlockAuthFormSocialProvider[] = [];
  if (showApple) {
    socialProviders.push({
      onPress: () => {
        oAuthAppleMutation.mutate();
      },
      pending: oAuthAppleMutation.isPending,
      provider: "apple",
    });
  }
  socialProviders.push({
    onPress: () => {
      oAuthGoogleMutation.mutate();
    },
    pending: oAuthGoogleMutation.isPending,
    provider: "google",
  });

  const socialConnections =
    !bare && renderSocialConnections != null && isNonEmpty(socialProviders)
      ? renderSocialConnections({
          compact: compact || card,
          disabled: busy,
          providers: socialProviders,
        })
      : null;

  if (mode === "twoFactor") {
    return (
      <BlockAuthFormFrame>
        <TwoFactorChallengeForm
          busy={busy}
          code={twoFactorCode}
          onCancel={() => {
            setTwoFactorCode("");
            setMode("signIn");
          }}
          onChangeCode={setTwoFactorCode}
          onSubmit={(values) => twoFactorVerifyMutation.mutate(values)}
          onToggleBackupCode={() => {
            setTwoFactorCode("");
            setTwoFactorUseBackupCode((previous) => !previous);
          }}
          useBackupCode={twoFactorUseBackupCode}
        />
      </BlockAuthFormFrame>
    );
  }

  if (mode === "verifyEmail") {
    return (
      <BlockAuthFormFrame>
        <VerifyEmailForm
          busy={busy}
          code={verifyCode}
          email={email}
          onCancel={() => setMode("signUp")}
          onChangeCode={setVerifyCode}
          onResend={() => resendVerificationMutation.mutate()}
          onSubmit={(values) => verifyEmailMutation.mutate(values)}
        />
      </BlockAuthFormFrame>
    );
  }

  if (mode === "reset") {
    return (
      <BlockAuthFormFrame>
        <ResetPasswordForm
          busy={busy}
          code={resetCode}
          onChangeCode={setResetCode}
          onChangePassword={setResetPassword}
          onSubmit={(values) => passwordResetMutation.mutate(values)}
          password={resetPassword}
        />
      </BlockAuthFormFrame>
    );
  }

  if (mode === "signIn") {
    return (
      <BlockAuthFormFrame>
        <SignInForm
          bare={bare}
          busy={busy}
          card={card}
          compact={compact}
          email={email}
          onChangeEmail={setEmail}
          onChangePassword={setPassword}
          onForgotPassword={() => forgotPasswordMutation.mutate()}
          onNavigateSignUp={() => setMode("signUp")}
          onSubmit={(values) => {
            void requestSignIn(values);
          }}
          password={password}
          socialConnections={socialConnections}
          submitPendingLabel={
            isCheckingLocalStudyData || emailPasswordMutation.isPending ? "Signing in…" : undefined
          }
        />
        <SignInReplaceWarningDialog
          getConnection={getActorConnectionForExport}
          onCancel={() => setPendingSignInValues(null)}
          onContinue={() => {
            const values = pendingSignInValues;
            setPendingSignInValues(null);
            if (values != null) {
              emailPasswordMutation.mutate(values);
            }
          }}
          open={pendingSignInValues != null}
        />
      </BlockAuthFormFrame>
    );
  }

  return (
    <BlockAuthFormFrame>
      <SignUpForm
        bare={bare}
        busy={busy}
        card={card}
        compact={compact}
        email={email}
        onChangeEmail={setEmail}
        onChangePassword={setPassword}
        onNavigateSignIn={() => setMode("signIn")}
        onSubmit={(values) => emailPasswordMutation.mutate(values)}
        password={password}
        socialConnections={socialConnections}
        submitPendingLabel={emailPasswordMutation.isPending ? "Creating account…" : undefined}
      />
    </BlockAuthFormFrame>
  );
}

/**
 * Every form surface carries credentials, verification codes or the account email, so the whole
 * frame is masked in session replays.
 */
function BlockAuthFormFrame({ children }: { children: ReactNode }) {
  return (
    <View className="w-full max-w-sm">
      <PostHogMaskView>{children}</PostHogMaskView>
    </View>
  );
}
